import {EventEmitter, Injectable} from "@angular/core";
import {OrganizationMapTemplate} from "./template/organization-map-template";
import {
  CustomFilterEvent,
  LoadProdGridDataCompleteEvent,
  ProdGridHeaderButton,
  RequestEvent,
  ResponseEvent,
  SelectRecordEvent
} from 'prod-grid';
import {IProdMap, MarkerClick, MarkerDblClick, MarkerVisible, ProdMarker} from 'prod-map';
import {HttpFilterParam} from 'prod-shared';
import {CarsComponent} from "../../components/cars/cars.component";
import {CarMapTemplate} from "./template/car-map-template";
import {SiteComponent} from "../../components/company/site/site.component";
import {SiteDetailComponent} from "../../components/company/site-detail/site-detail.component";
import {HubMapTemplate} from "./template/hub-map-template";
import {Subject, Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class GridMapService implements IProdMap {
  markers$?: EventEmitter<Map<number, ProdMarker>> = new EventEmitter<Map<number, ProdMarker>>();
  markerSelected$: EventEmitter<number[]> = new EventEmitter<number[]>();
  mapFilter$: Subject<boolean> = new Subject<boolean>();
  mapBounds$: Subject<any> = new Subject();
  componentSubscription: Subscription;
  private parentComponent;

  constructor(private translate: TranslateService) {
  }

  init(component) {
    this.markers$ = new EventEmitter<Map<number, ProdMarker>>();
    this.parentComponent = component;

    if (this.componentSubscription !== undefined) {
      this.componentSubscription.unsubscribe();
    }

    this.componentSubscription = component.inputAction$.subscribe(event => {
      switch (event.constructor.name) {
        case LoadProdGridDataCompleteEvent.name: {
          const latLng = JSON.parse(event.httpExtraParams.get('filter')).find(f => f.property === 'latLng');
          if (latLng) {
            this.mapBounds$.next(latLng.value);
          }
          this.parentComponent.prodGridDataService.getProdGridData((<LoadProdGridDataCompleteEvent>event).httpExtraParams.set('limit', -1)).subscribe((response) => {
            const markers: Map<number, ProdMarker> = new Map();
            response.data.result.forEach(d => {
              switch (component.constructor.name) {
                case SiteComponent.name: {
                  markers.set(d['id'], new ProdMarker({
                      id: d['id'],
                      name: d['name'],
                      lat: d['latitude'],
                      lng: d['longitude'],
                      icon: OrganizationMapTemplate.getIcon(d),
                      tooltip: OrganizationMapTemplate.getTooltip(d),
                      popup: OrganizationMapTemplate.getPopup(d),
                      pulse: false,
                      radius: 0
                    }
                  ));
                  break;
                }
                case CarsComponent.name: {
                  markers.set(d['id'], new ProdMarker({
                      id: d['id'],
                      name: d['name'],
                      lat: d['equipment']['latitude'],
                      lng: d['equipment']['longitude'],
                      icon: CarMapTemplate.getIcon(d),
                      tooltip: CarMapTemplate.getTooltip(d),
                      popup: CarMapTemplate.getPopup(d),
                      pulse: false,
                      radius: 0
                    }
                  ));
                  break;
                }
                case SiteDetailComponent.name: {
                  markers.set(d['id'], new ProdMarker({
                      id: d['id'],
                      name: d['name'],
                      lat: d['hub']['latitude'],
                      lng: d['hub']['longitude'],
                      icon: HubMapTemplate.getIcon(d),
                      tooltip: HubMapTemplate.getTooltip(d),
                      popup: HubMapTemplate.getPopup(d),
                      pulse: true,
                      radius: d['hub']['radius']
                    }
                  ));
                }
              }
            });
            this.markers$.emit(markers);
          });
        }
      }
    });

    this.parentComponent.prodGridHeaderButtons.push(
      new ProdGridHeaderButton({
        id: 'latLng',
        title: this.translate.instant('map_filter_button.tooltip'),
        text: this.translate.instant('map_filter_button.text'),
        icon: 'fa fa-map',
        clickEvent: (isChecked) => {
          this.mapFilter$.next(isChecked);
          if (!isChecked) {
            this.parentComponent.inputAction$.emit(new CustomFilterEvent([
              new HttpFilterParam({
                property: 'latLng',
                operator: 'inBounds',
                value: []
              })
            ]));
          }
        },
        disabled: false,
        isToggleButton: true,
        checked: false
      })
    );
  }

  prodMapEvents(event) {
    switch (event.constructor.name) {
      case MarkerDblClick.name:
      case MarkerClick.name: {
        this.parentComponent.inputAction$.emit(new SelectRecordEvent(event.marker.id));
        break;
      }
      case MarkerVisible.name: {
        const mapFilterBtn = this.parentComponent.prodGridHeaderButtons.find(btn => btn.id === 'latLng');
        mapFilterBtn.value = event.bounds;
        this.parentComponent.inputAction$.emit(new CustomFilterEvent([
          new HttpFilterParam({
            property: 'latLng',
            operator: 'inBounds',
            value: event.bounds
          })
        ]));
      }
    }
  }

  outputActionEvents(event: { event: RequestEvent | ResponseEvent, value?: any }) {
    switch (event.event) {
      case ResponseEvent.RESPONSE_SELECTED_RECORDS: {
        this.markerSelected$.emit(Array.from(event.value.records.getAll().keys()));
        break;
      }
      case ResponseEvent.RESPONSE_RESET_FILTERS: {
        this.mapFilter$.next(false);
      }
    }
  }
}
